import { t } from '../../strings';
import { el, ensureStyle } from '../dom';
import { icon, type IconName } from './icons';

ensureStyle(
  'banner-css',
  `
  /* Ink is a variable so the two tones differ by two declarations rather than by
     a parallel set of rules for the message, the icon and the buttons. */
  .banner { --banner-ink:var(--on-danger);
    position:fixed; left:0; right:0; bottom:0; z-index:50;
    max-width:480px; margin:0 auto; background:var(--danger); color:var(--banner-ink);
    font-size:12.5px; line-height:1.4;
    padding:12px 14px calc(12px + env(safe-area-inset-bottom));
    display:flex; gap:10px; align-items:center; }
  /* Not every interruption is a failure. An available update is news, so it
     takes the app's own green rather than the red kept for "your progress
     didn't save" — a colour that stops meaning anything if everything wears it. */
  .banner.info { --banner-ink:var(--on-dark); background:var(--moss-mid); }
  .banner .msg { flex:1; }
  .banner .icon { opacity:.85; }
  .banner button { background:var(--overlay-strong); color:var(--banner-ink);
    border-color:transparent; padding:7px 11px; font-size:0.78em; }
`,
);

let current: HTMLElement | null = null;

/** Which kind of interruption this is — it picks the colour and the glyph. */
export type BannerTone = 'error' | 'info';

const TONE_ICON: Record<BannerTone, IconName> = {
  error: 'alert',
  info: 'download',
};

/**
 * A single dismissible notice pinned above the nav bar.
 *
 * Two tones. `error` is a background write that didn't land — progress lives on
 * the server, so a failed save must never be silent, but it also must not
 * interrupt a quiz. `info` is news rather than trouble: a new version waiting to
 * be taken. Neither steals focus, which is the whole reason this exists instead
 * of confirm() — a modal thrown up mid-question discards the answer the learner
 * was in the middle of giving, and arrives on a screen it can't see.
 */
export function showBanner(
  message: string,
  action?: { label: string; run: () => void },
  tone: BannerTone = 'error',
): void {
  current?.remove();
  const dismiss = el('button.btn.btn-sm', { onclick: () => hideBanner() }, t('common.dismiss'));
  current = el(
    `div.banner.${tone}`,
    {},
    icon(TONE_ICON[tone], { size: 18 }),
    el('div.msg', {}, message),
    action && el('button.btn.btn-sm', { onclick: action.run }, action.label),
    dismiss,
  );
  document.body.append(current);
}

export function hideBanner(): void {
  current?.remove();
  current = null;
}
